// ============================================================
// LiveAnnouncer.jsx — the admin portal's single pair of ARIA live regions.
//
// Ported from the user SWA (frontend/src/components/a11y/LiveAnnouncer.jsx,
// 2026-08-11 WCAG 4.1.3 pass) for the 2026-08-29 admin status-message
// sweep. The two files are deliberately near-identical — if you fix a
// defect in one, fix the other.
//
// WHY A PERSISTENT REGION RATHER THAN role="status" ON EACH MESSAGE
// ------------------------------------------------------------------
// A React status message is CONDITIONALLY RENDERED, so the live region
// is inserted into the DOM *already carrying its text*. Screen readers
// watch a live region for MUTATIONS; a region that did not exist a
// moment ago has no "before" state to diff against, and several
// SR/browser pairings announce nothing at all. The reliable pattern is
// a region that is ALWAYS mounted and EMPTY, whose text content is
// then changed. This component mounts once at the app root (App.js,
// above the Routes so it survives navigation) and stays empty until
// something calls announce(). StatusMessage keeps its inline
// role/aria-live as well — the inline role is the auditable artefact,
// the persistent region is the guarantee.
//
// IDENTICAL CONSECUTIVE MESSAGES: setting a live region to the text it
// already holds is not a mutation, so each region holds TWO child
// nodes and the text alternates between them — a guaranteed DOM
// mutation even when the string is unchanged (two identical errors are
// two real events).
//
// POLITENESS: 'polite' waits for a pause (loading, empty, saved);
// 'assertive' interrupts (errors). Assertive is deliberately rare.
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';

// Queue anything announced before the component has mounted (a fetch
// that rejects during the very first render pass) and flush on mount.
let emit = null;
const preMountQueue = [];

// Long enough for any SR to pick the message up, short enough that a
// user browsing later does not meet a stale "Loading…" in the tree.
const CLEAR_MS = 7000;

/**
 * Announce a status message to assistive technology.
 * @param {string} message
 * @param {'polite'|'assertive'} politeness
 */
export function announce(message, politeness = 'polite') {
  if (!message || typeof message !== 'string') return;
  const trimmed = message.trim();
  if (!trimmed) return;
  const level = politeness === 'assertive' ? 'assertive' : 'polite';
  if (emit) emit(trimmed, level);
  else preMountQueue.push([trimmed, level]);
}

// Test seam: drop any queued-but-unflushed messages between tests.
export function __resetAnnouncerForTests() {
  emit = null;
  preMountQueue.length = 0;
}

export default function LiveAnnouncer() {
  // slot alternates 0/1 so identical consecutive text still mutates.
  const [polite, setPolite] = useState({ text: '', slot: 0 });
  const [assertive, setAssertive] = useState({ text: '', slot: 0 });

  const push = useCallback((text, level) => {
    const setter = level === 'assertive' ? setAssertive : setPolite;
    setter((prev) => ({ text, slot: prev.slot === 0 ? 1 : 0 }));
  }, []);

  useEffect(() => {
    emit = push;
    while (preMountQueue.length) {
      const [text, level] = preMountQueue.shift();
      push(text, level);
    }
    return () => { emit = null; };
  }, [push]);

  // Clear each region a while after it last changed. Two separate
  // effects so a polite message does not reset the assertive timer.
  useEffect(() => {
    if (!polite.text) return undefined;
    const t = setTimeout(() => setPolite((p) => ({ ...p, text: '' })), CLEAR_MS);
    return () => clearTimeout(t);
  }, [polite.text, polite.slot]);

  useEffect(() => {
    if (!assertive.text) return undefined;
    const t = setTimeout(() => setAssertive((p) => ({ ...p, text: '' })), CLEAR_MS);
    return () => clearTimeout(t);
  }, [assertive.text, assertive.slot]);

  return (
    <>
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="live-region-polite"
      >
        <div>{polite.slot === 0 ? polite.text : ''}</div>
        <div>{polite.slot === 1 ? polite.text : ''}</div>
      </div>
      <div
        className="sr-only"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        data-testid="live-region-assertive"
      >
        <div>{assertive.slot === 0 ? assertive.text : ''}</div>
        <div>{assertive.slot === 1 ? assertive.text : ''}</div>
      </div>
    </>
  );
}
